import React from 'react';
import {
  BadgeCheck,
  Download,
  Eye,
  HardDrive,
  Loader2,
  Paperclip,
  Pencil,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import ContractFileVersionFields from './ContractFileVersionFields';

export default function ContractFilesTab({
  apiBase,
  canWriteContracts,
  cancelFileEdit,
  contractId,
  deletingFileId,
  editingFileId,
  fileDraft,
  fileTiles,
  handleMarkAsExecuted,
  markingFileId,
  openFileDeleteConfirm,
  openFileEdit,
  openFilePreview,
  openUploadModal,
  saveFileMeta,
  savingFileMeta,
  setFileDraft,
}) {
  // Reference documents (doc-46) sit in their own section — supporting
  // material read by structured-data runs, not versions of the contract.
  const lifecycleTiles = fileTiles.filter((f) => f.fileKind !== 'reference');
  const referenceTiles = fileTiles.filter((f) => f.fileKind === 'reference');

  const renderFileRow = (f, i, tiles) => {
    const isReference = f.fileKind === 'reference';
    const canMarkExecuted = canWriteContracts && f.fileId && !f.signed && f.tone !== 'folder' && !isReference;
    const canEditMeta = canWriteContracts && f.fileId && f.tone !== 'folder';
    const canDeleteFile = canWriteContracts && f.fileId && f.tone !== 'folder';
    const isEditingThis = editingFileId === f.fileId;
    // Direction folds into the hint line rather than a
    // colored chip, so the row carries less visual noise.
    const directionLabel = {
      inbound: 'from counterparty',
      outbound: 'sent to counterparty',
      internal: 'internal',
    }[f.direction];
    // Executed files surface their status as a green chip by the
    // title, so their subline carries just the date (+ direction).
    // Everything else keeps the full hint.
    const subLine = ((f.signed || isReference) && f.tone !== 'folder')
      ? [f.dateLabel, directionLabel].filter(Boolean).join(' · ')
      : [f.hint, directionLabel].filter(Boolean).join(' · ');
    return (
      <div
        key={f.key}
        style={{
          borderBottom: i === tiles.length - 1 ? 0 : '0.5px solid var(--border-primary)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 24px',
          }}
        >
          {/* File actions are explicit buttons beside the metadata. */}
          <div
            style={{
              display: 'flex',
              flex: 1,
              minWidth: 0,
              alignItems: 'center',
              gap: 10,
              color: 'inherit',
            }}
          >
            {(() => {
              if (f.tone === 'folder') {
                return (
                  <span
                    style={{
                      width: 30, height: 36, borderRadius: 4,
                      background: 'var(--surface-blue)',
                      color: 'var(--text-blue)',
                      display: 'grid', placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <HardDrive className="w-4 h-4" />
                  </span>
                );
              }
              const ext = (f.name || '').split('.').pop().toLowerCase();
              const isWord = ext === 'docx' || ext === 'doc';
              const label = isWord ? ext.toUpperCase() : 'PDF';
              const bg = isWord ? 'var(--color-blue-700)' : 'var(--color-red-600)';
              return (
                <span
                  style={{
                    width: 30, height: 36, borderRadius: 4,
                    background: bg, color: 'var(--color-base-white)',
                    display: 'grid', placeItems: 'center',
                    fontSize: 9, fontWeight: 600, letterSpacing: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {label}
                </span>
              );
            })()}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 450, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{f.name}</span>
                {/* Status as a chip by the title (clearly a label,
                    not an action): folder → blue, executed → green,
                    reference → purple, plain drafts → none. */}
                {f.tone === 'folder' ? (
                  <span className="chip sq blue" style={{ flexShrink: 0 }}>Folder</span>
                ) : f.signed ? (
                  <span className="chip green sq" style={{ flexShrink: 0 }} title="Executed / signed">Executed</span>
                ) : isReference ? (
                  <span className="chip purple sq" style={{ flexShrink: 0 }} title="Reference document — read by structured-data runs, not a contract version">Reference</span>
                ) : null}
              </div>
              {subLine && <div className="muted mono" style={{ fontSize: 11 }}>{subLine}</div>}
              {f.fileNotes && (
                <div
                  className="muted"
                  style={{ fontSize: 11, marginTop: 2, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={f.fileNotes}
                >
                  {f.fileNotes}
                </div>
              )}
            </div>
          </div>
          {/* Action group — only renders the icons that
              are applicable to this row. No empty
              placeholders, so executed files (no
              Mark-as-executed needed) show two icons
              flush instead of leaving a gap. */}
          <div
            className="row"
            style={{
              gap: 6,
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {openFilePreview && (f.fileId || f.key === 'upload') && (
              <button onClick={() => openFilePreview(f)} className="icon-btn xs" title="Preview this file" aria-label={`Preview ${f.name}`}>
                <Eye className="w-3.5 h-3.5" />
              </button>
            )}
            {canEditMeta && (
              <button
                onClick={() => (isEditingThis ? cancelFileEdit() : openFileEdit(f))}
                className="icon-btn xs"
                title={isEditingThis ? 'Cancel edit' : 'Edit date, direction, and notes'}
                aria-label={isEditingThis ? 'Cancel edit' : 'Edit date, direction, and notes'}
              >
                {isEditingThis ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
              </button>
            )}
            {canMarkExecuted && (
              <button
                onClick={() => handleMarkAsExecuted(f.fileId)}
                disabled={markingFileId === f.fileId}
                className="icon-btn xs"
                title={markingFileId === f.fileId ? 'Marking…' : 'Mark as executed/signed'}
                aria-label="Mark as executed/signed"
                style={{ color: 'var(--text-green)' }}
              >
                <BadgeCheck className="w-3.5 h-3.5" />
              </button>
            )}
            {f.fileId && f.tone !== 'folder' && (
              // Direct file download. Plain <a> with download
              // attr so the browser saves the bytes instead
              // of navigating away. Backend streams the bytes
              // from Postgres (file_blobs).
              <a
                href={`${apiBase}/contracts/${contractId}/files/${f.fileId}/download`}
                className="icon-btn xs"
                title="Download this file"
                aria-label="Download this file"
                download={f.name || ''}
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="w-3.5 h-3.5" />
              </a>
            )}
            {canDeleteFile && (
              <button
                onClick={() => openFileDeleteConfirm(f)}
                disabled={deletingFileId === f.fileId}
                className="icon-btn xs"
                title={deletingFileId === f.fileId ? 'Deleting…' : 'Delete this file'}
                aria-label="Delete this file"
                style={{ color: 'var(--color-red-500)' }}
              >
                {deletingFileId === f.fileId
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        </div>
        {isEditingThis && (
          <div
            style={{
              padding: '0 24px 16px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12,
              // Align inputs at the top of each column so the
              // date and direction fields line up visually
              // even when their hint-text lines differ.
              alignItems: 'start',
            }}
          >
            <ContractFileVersionFields
              documentDate={fileDraft.documentDate}
              direction={fileDraft.direction}
              fileNotes={fileDraft.fileNotes}
              onChange={(field, value) => setFileDraft((draft) => ({
                ...draft,
                [field]: value,
              }))}
              style={{ gridColumn: '1 / -1' }}
              intro={(
                <>
                  These describe <em>this file version</em> — not the underlying contract.
                  The contract's effective/expiration dates are on the Term &amp; Renewal card.
                </>
              )}
              dateHint={(
                <>
                  Date on <em>this draft</em> (cover letter, header/footer, “Draft of&nbsp;___”). Not the contract's effective date.
                </>
              )}
              notesPlaceholder="Short context about this version — e.g. cover email said 'please review by Friday'"
            />
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn ghost xs" onClick={cancelFileEdit}>Cancel</button>
              <button
                className="btn primary xs"
                onClick={() => saveFileMeta(f.fileId)}
                disabled={savingFileMeta}
              >
                {savingFileMeta ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
            <div className="card">
              <div className="card-body flush">
                {fileTiles.length === 0 ? (
                  <div className="muted" style={{ padding: 40, textAlign: 'center', fontSize: 13 }}>
                    No files attached.
                  </div>
                ) : (
                  lifecycleTiles.map((f, i) => renderFileRow(f, i, lifecycleTiles))
                )}
                {referenceTiles.length > 0 && (
                  <>
                    <div
                      className="row"
                      style={{
                        gap: 8,
                        padding: '10px 24px 6px',
                        borderTop: lifecycleTiles.length > 0 ? '0.5px solid var(--border-primary)' : 0,
                      }}
                    >
                      <Paperclip className="w-3.5 h-3.5" style={{ color: 'var(--icon-secondary)' }} />
                      <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.7, color: 'var(--text-tertiary)' }}>
                        Reference documents
                      </span>
                      <span className="muted" style={{ fontSize: 11 }}>
                        Supporting material — read by structured-data runs
                      </span>
                    </div>
                    {referenceTiles.map((f, i) => renderFileRow(f, i, referenceTiles))}
                  </>
                )}
                {/* Single "Upload file" entry point — opens a modal
                    with a drag-drop zone. After file pick the modal
                    shows analysis + an editable form (executed flag,
                    direction, document date, notes) so the user can
                    confirm the classifier's read or override. */}
                {canWriteContracts && (
                  <div
                    className="row"
                    style={{
                      padding: '12px 24px',
                      borderTop: fileTiles.length > 0 ? '0.5px solid var(--border-primary)' : 0,
                      gap: 8,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    <button
                      onClick={openUploadModal}
                      className="btn outline xs"
                      title="Add a draft or executed version, or a reference document, to this contract"
                    >
                      <Upload className="w-3 h-3" /> Add file
                    </button>
                  </div>
                )}
              </div>
            </div>
  );
}
